<template>
  <v-container class="modern-view pb-12">
    <!-- Premium Header -->
    <v-row class="mb-8 align-center px-4 pt-4">
      <div class="d-flex align-center">
        <v-avatar color="primary" size="48" class="mr-4 shadow-sm border-white-sm">
          <v-icon color="white">fas fa-sync-alt</v-icon>
        </v-avatar>
        <div>
          <div class="text-h4 font-weight-black primary--text lh-1">Facturación OT <span class="secondary--text">Relbase</span></div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Control de OTs y documentos tributarios electrónicos</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <div class="d-flex align-center">
        <export-relbase-sync-report :filter="filter" :search="search" :options="options" :disabled="loading"/>
      </div>
    </v-row>

    <!-- Modern Filters Card -->
    <v-card outlined class="modern-card pa-6 mb-8 shadow-sm">
      <v-row dense>
        <v-col cols="12" md="3">
          <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Fecha OT Desde</div>
          <v-text-field
            hide-details
            :disabled="loading"
            dense
            v-model='filter.created_after'
            outlined
            type="date"
            class="modern-input"
          />
        </v-col>
        <v-col cols="12" md="3">
          <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Fecha OT Hasta</div>
          <v-text-field
            hide-details
            :disabled="loading"
            dense
            v-model='filter.created_before'
            outlined
            type="date"
            class="modern-input"
          />
        </v-col>
        <v-col cols="12" md="2">
          <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Estado</div>
          <v-select
            v-model="filter.is_synced"
            :items="sync_options"
            item-text="text"
            item-value="value"
            placeholder="Todos"
            outlined
            clearable
            dense
            hide-details
            class="modern-input"
          />
        </v-col>
        <v-col cols="12" md="4">
           <div class="caption font-weight-black grey--text text-uppercase mb-1 ml-1">Buscar OT / Cliente</div>
           <v-text-field
             v-model="search"
             placeholder="Ej: 1234 o Nombre Cliente"
             outlined
             dense
             hide-details
             prepend-inner-icon="fas fa-search"
             class="modern-input"
             clearable
           />
        </v-col>
      </v-row>
    </v-card>

    <!-- Data Table -->
    <v-card outlined class="modern-card shadow-sm border-0 overflow-hidden">
      <v-data-table
        :headers="headers"
        :items="items"
        :options.sync="options"
        :server-items-length="totalItems"
        :loading="loading"
        class="modern-table"
        :footer-props="{ 'items-per-page-options': [20, 50, 100] }"
      >
        <template v-slot:item.id="{ item }">
          <span class="font-weight-black primary--text">#{{ item.id }}</span>
        </template>
        <template v-slot:item.sales_note_id="{ item }">
          <span class="font-weight-bold grey--text text--darken-2">{{ item.sales_note_id || '—' }}</span>
        </template>
        <template v-slot:item.created_at="{ item }">
          {{ item.created_at | moment("DD/MM/YYYY") }}
        </template>
        <template v-slot:item.total_sale="{ item }">
          {{ item.total_sale | moneda }}
        </template>
        <template v-slot:item.total_facturado="{ item }">
          <span :class="diffClass(item)">{{ item.total_facturado | moneda }}</span>
        </template>
        <template v-slot:item.is_synced="{ item }">
           <v-chip
             small
             :color="item.is_synced ? 'success' : 'warning'"
             class="text-uppercase font-weight-bold"
             label
           >
             <v-icon left x-small>{{ item.is_synced ? 'fas fa-check-circle' : 'fas fa-exclamation-triangle' }}</v-icon>
             {{ item.is_synced ? 'Facturado' : 'Sin Facturar' }}
           </v-chip>
        </template>
        <template v-slot:item.relbase_folio="{ item }">
          <v-btn
            v-if="item.relbase_folio"
            text
            small
            color="primary"
            class="font-weight-bold"
            @click="viewDTE(item)"
          >
            {{ item.relbase_folio }}
          </v-btn>
          <span v-else class="grey--text caption italic">Sin factura electrónica</span>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import { fetchRelbaseSyncReport } from '@/api/relbase'
import ExportRelbaseSyncReport from '@/components/relbase/ExportRelbaseSyncReport.vue'
import debounce from 'lodash/debounce'

export default {
  name: 'RelbaseSyncReport',
  components: { ExportRelbaseSyncReport },
  data() {
    return {
      items: [],
      totalItems: 0,
      loading: false,
      search: '',
      options: {
        page: 1,
        itemsPerPage: 20,
        sortBy: ['id'],
        sortDesc: [false]
      },
      filter: {
        created_after: '',
        created_before: '',
        is_synced: null,
        customer: null,
        seller: null
      },
      sync_options: [
        { text: 'Facturado', value: true },
        { text: 'Sin Facturar', value: false }
      ],
      headers: [
        { text: 'Nro OT', value: 'id', align: 'start' },
        { text: 'Cotización', value: 'sales_note_id', align: 'center' },
        { text: 'Fecha', value: 'created_at' },
        { text: 'Cliente', value: 'customer_name' },
        { text: 'Proyecto', value: 'project_name' },
        { text: 'Total Vendido', value: 'total_sale', align: 'end' },
        { text: 'Total Facturado', value: 'total_facturado', align: 'end' },
        { text: 'Folio(s) Relbase', value: 'relbase_folio', align: 'center', sortable: false },
        { text: 'Estado', value: 'is_synced', sortable: false, align: 'center' }
      ]
    }
  },
  watch: {
    options: { handler() { this.fetchData() }, deep: true },
    filter: {
      handler() {
        this.options.page = 1
        this.fetchData()
      },
      deep: true
    },
    search: {
      handler: debounce(function() {
        this.options.page = 1
        this.fetchData()
      }, 500)
    }
  },
  methods: {
    async fetchData() {
      this.loading = true
      const { page, itemsPerPage, sortBy, sortDesc } = this.options

      const params = {
        page,
        page_size: itemsPerPage,
        ordering: (sortDesc && sortDesc[0]) ? sortBy[0] : `-${sortBy[0]}`,
        search: this.search,
        created_at_after: this.filter.created_after,
        created_at_before: this.filter.created_before,
        is_synced: this.filter.is_synced,
        customer: this.filter.customer,
        seller: this.filter.seller
      }

      try {
        const data = await fetchRelbaseSyncReport(params)
        this.items = data.results || []
        this.totalItems = data.count || 0
      } catch (e) {
        console.error("Error fetching Relbase report:", e)
      } finally {
        this.loading = false
      }
    },
    viewDTE(item) {
      this.$router.push({ name: 'RelbaseDocumentsList', query: { search: item.relbase_folio } })
    },
    diffClass(item) {
      if (!item.total_sale || !item.total_facturado) return ''
      const diff = Math.abs(item.total_facturado - item.total_sale)
      // Tolerancia del 1% para diferencias por redondeo de IVA
      if (diff / item.total_sale > 0.01) return 'amber--text text--darken-3 font-weight-bold'
      return 'success--text font-weight-bold'
    }
  }
}
</script>

<style scoped>
.modern-view { font-family: 'Outfit', sans-serif !important; }
.modern-card { border-radius: 16px !important; border-color: rgba(0,0,0,0.06) !important; }
.border-white-sm { border: 3px solid white !important; }
.shadow-sm { box-shadow: 0 4px 20px rgba(0,0,0,0.04) !important; }
.lh-1 { line-height: 1 !important; }
.tracking-widest { letter-spacing: 1.5px; }
.modern-input >>> .v-input__control >>> .v-input__slot { border-radius: 10px !important; background-color: #f8fafc !important; }
.modern-table >>> thead th { background-color: #f8fafc !important; color: #64748b !important; text-transform: uppercase !important; font-size: 11px !important; font-weight: 800 !important; letter-spacing: 1px !important; }
.italic { font-style: italic; }
</style>
